import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { api, errorMsg, colones, ESTADO_PEDIDO_LABEL, fmtFechaCorta } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Panel } from "@/components/Panel";
import { Combobox } from "@/components/Combobox";
import { RepuestoPickerDialog } from "@/components/RepuestoPickerDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Plus, Trash2, PackagePlus, Ban, History } from "lucide-react";

const ORDEN_ESTADOS = ["solicitado", "pedido_fabrica", "en_transito", "recibido", "entregado"];
const hoyIso = () => new Date().toISOString().slice(0, 10);

export default function PedidoEspecialDetalle() {
  const { id } = useParams();
  const esNuevo = id === "nuevo";
  const location = useLocation();
  const navigate = useNavigate();
  const { tienePermiso } = useAuth();
  const pre = location.state || {};

  const [vehiculos, setVehiculos] = useState([]);
  const [trabajosVehiculo, setTrabajosVehiculo] = useState([]);
  const [pedido, setPedido] = useState(null);
  const [cargando, setCargando] = useState(!esNuevo);
  const [guardando, setGuardando] = useState(false);

  const [vehiculoId, setVehiculoId] = useState(pre.vehiculoId || "");
  const [clienteId, setClienteId] = useState(pre.clienteId || "");
  const [trabajoId, setTrabajoId] = useState(pre.trabajoId || "");
  const [fechaEstimada, setFechaEstimada] = useState("");
  const [notas, setNotas] = useState("");
  const [repuestos, setRepuestos] = useState(
    (pre.repuestosPre || []).map((r) => ({ repuesto_id: r.ref_id || null, codigo: r.codigo || "",
      nombre: r.nombre, cantidad: r.cantidad || 1, precio_unitario_iva: 0, recibido: false })));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [manual, setManual] = useState({ codigo: "", nombre: "" });

  const [adelantoMonto, setAdelantoMonto] = useState("");
  const [adelantoFecha, setAdelantoFecha] = useState(hoyIso());
  const [adelantoFormaPago, setAdelantoFormaPago] = useState("");
  const [adelantoNota, setAdelantoNota] = useState("");
  const [comprobante, setComprobante] = useState(null);

  const [pagoDialog, setPagoDialog] = useState(false);
  const [pagoTipo, setPagoTipo] = useState("abono");
  const [pagoMonto, setPagoMonto] = useState("");
  const [pagoFecha, setPagoFecha] = useState(hoyIso());
  const [pagoFormaPago, setPagoFormaPago] = useState("");
  const [pagoNota, setPagoNota] = useState("");
  const [pagoComprobante, setPagoComprobante] = useState(null);
  const [registrandoPago, setRegistrandoPago] = useState(false);

  const [estadoNuevo, setEstadoNuevo] = useState("");
  const [estadoNota, setEstadoNota] = useState("");
  const [cancelarOpen, setCancelarOpen] = useState(false);
  const [cancelarNota, setCancelarNota] = useState("");

  useEffect(() => { api.get("/vehiculos").then((r) => setVehiculos(r.data)).catch(() => {}); }, []);

  const cargarPedido = useCallback(async () => {
    if (esNuevo) return;
    setCargando(true);
    try {
      const { data } = await api.get(`/pedidos-especiales/${id}`);
      setPedido(data);
      setVehiculoId(data.vehiculo_id); setClienteId(data.cliente_id); setTrabajoId(data.trabajo_id || "");
      setFechaEstimada(data.fecha_estimada_arribo || ""); setNotas(data.notas || "");
      setRepuestos(data.repuestos.map((r) => ({ ...r })));
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setCargando(false); }
  }, [id, esNuevo]);
  useEffect(() => { cargarPedido(); }, [cargarPedido]);

  useEffect(() => {
    if (!vehiculoId) { setTrabajosVehiculo([]); return; }
    api.get("/trabajos", { params: { vehiculo_id: vehiculoId, estado: "activo" } })
      .then((r) => setTrabajosVehiculo(r.data)).catch(() => setTrabajosVehiculo([]));
  }, [vehiculoId]);

  const vehiculoOpciones = useMemo(() => vehiculos.map((v) => ({
    value: v.id, label: `${v.placa} · ${v.modelo?.nombre_mostrar || "—"} · ${v.cliente?.nombre || "—"}` })), [vehiculos]);
  const vehiculoSel = useMemo(() => vehiculos.find((v) => v.id === vehiculoId), [vehiculos, vehiculoId]);

  const onVehiculoChange = (vid) => {
    setVehiculoId(vid);
    const v = vehiculos.find((x) => x.id === vid);
    if (v?.cliente_id) setClienteId(v.cliente_id);
  };

  const trabajoOpciones = useMemo(() => trabajosVehiculo.map((t) => (
    { value: t.id, label: `Orden #${String(t.numero).padStart(6, "0")}` })), [trabajosVehiculo]);

  const agregarDelCatalogo = (sel) => {
    setRepuestos((prev) => {
      const manuales = prev.filter((r) => !r.repuesto_id);
      const nuevos = sel.map((s) => {
        const ex = prev.find((r) => r.repuesto_id === s.ref_id);
        return { id: ex?.id, repuesto_id: s.ref_id, codigo: s.codigo || "", nombre: s.nombre,
          cantidad: s.cantidad, precio_unitario_iva: ex?.precio_unitario_iva ?? 0, recibido: ex?.recibido || false };
      });
      return [...nuevos, ...manuales];
    });
  };

  const agregarManual = () => {
    if (!manual.nombre.trim()) return;
    setRepuestos((prev) => [...prev, { repuesto_id: null, codigo: manual.codigo, nombre: manual.nombre,
      cantidad: 1, precio_unitario_iva: 0, recibido: false }]);
    setManual({ codigo: "", nombre: "" });
  };

  const quitarRepuesto = (idx) => setRepuestos((prev) => prev.filter((_, i) => i !== idx));
  const setRepCampo = (idx, campo, valor) =>
    setRepuestos((prev) => prev.map((r, i) => (i === idx ? { ...r, [campo]: valor } : r)));

  const total = useMemo(() => repuestos.reduce(
    (a, r) => a + Number(r.cantidad || 0) * Number(r.precio_unitario_iva || 0), 0), [repuestos]);

  const puedeEditar = esNuevo ? tienePermiso("pedidos.crear")
    : !!(pedido && pedido.estado !== "cancelado" && tienePermiso("pedidos.editar"));

  const [sinAdelantoOpen, setSinAdelantoOpen] = useState(false);

  const crear = async () => {
    setGuardando(true);
    try {
      const fd = new FormData();
      fd.append("cliente_id", clienteId); fd.append("vehiculo_id", vehiculoId); fd.append("trabajo_id", trabajoId || "");
      fd.append("fecha_estimada_arribo", fechaEstimada || ""); fd.append("notas", notas || "");
      fd.append("repuestos", JSON.stringify(repuestos.map(
        ({ repuesto_id, codigo, nombre, cantidad, precio_unitario_iva }) =>
          ({ repuesto_id, codigo, nombre, cantidad, precio_unitario_iva }))));
      if (adelantoMonto && Number(adelantoMonto) > 0) {
        fd.append("adelanto_monto", adelantoMonto); fd.append("adelanto_fecha", adelantoFecha);
        fd.append("adelanto_forma_pago", adelantoFormaPago); fd.append("adelanto_nota", adelantoNota);
        if (comprobante) fd.append("comprobante", comprobante);
      }
      const { data } = await api.post("/pedidos-especiales", fd);
      toast.success(`Pedido especial ${data.numero_fmt} creado`);
      navigate(`/pedidos-especiales/${data.id}`);
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setGuardando(false); setSinAdelantoOpen(false); }
  };

  const intentarCrear = () => {
    if (!vehiculoId) { toast.error("Seleccione un vehículo"); return; }
    if (!repuestos.length) { toast.error("Agregue al menos un repuesto"); return; }
    if (!adelantoMonto || Number(adelantoMonto) <= 0) { setSinAdelantoOpen(true); return; }
    crear();
  };

  const guardarCambios = async () => {
    setGuardando(true);
    try {
      const { data } = await api.patch(`/pedidos-especiales/${id}`, {
        repuestos: repuestos.map(({ id: rid, repuesto_id, codigo, nombre, cantidad, precio_unitario_iva }) =>
          ({ id: rid, repuesto_id, codigo, nombre, cantidad, precio_unitario_iva })),
        fecha_estimada_arribo: fechaEstimada || null, notas, trabajo_id: trabajoId || null,
      });
      setPedido(data); setRepuestos(data.repuestos.map((r) => ({ ...r })));
      toast.success("Cambios guardados");
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setGuardando(false); }
  };

  const marcarRecibido = async (rid, recibido) => {
    try {
      const { data } = await api.post(`/pedidos-especiales/${id}/repuestos/${rid}/recibido`, { recibido });
      setPedido(data); setRepuestos(data.repuestos.map((r) => ({ ...r })));
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const cambiarEstado = async () => {
    if (!estadoNuevo) return;
    try {
      const { data } = await api.post(`/pedidos-especiales/${id}/estado`, { estado: estadoNuevo, nota: estadoNota });
      setPedido(data); setEstadoNuevo(""); setEstadoNota(""); toast.success("Estado actualizado");
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const cancelar = async () => {
    try {
      const { data } = await api.post(`/pedidos-especiales/${id}/cancelar`, { nota: cancelarNota });
      setPedido(data); setCancelarOpen(false); toast.success("Pedido cancelado");
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const registrarPago = async () => {
    if (!pagoMonto || Number(pagoMonto) <= 0) { toast.error("Ingrese un monto válido"); return; }
    setRegistrandoPago(true);
    try {
      const fd = new FormData();
      fd.append("tipo", pagoTipo); fd.append("monto", pagoMonto); fd.append("fecha", pagoFecha);
      fd.append("forma_pago", pagoFormaPago); fd.append("nota", pagoNota);
      if (pagoComprobante) fd.append("comprobante", pagoComprobante);
      const { data } = await api.post(`/pedidos-especiales/${id}/pagos`, fd);
      setPedido(data); setPagoDialog(false);
      setPagoMonto(""); setPagoFormaPago(""); setPagoNota(""); setPagoComprobante(null);
      toast.success("Pago registrado");
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setRegistrandoPago(false); }
  };

  const verComprobante = async (compId) => {
    try {
      const r = await api.get(`/comprobantes/${compId}`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const siguientesEstados = useMemo(() => {
    if (!pedido) return [];
    const idx = ORDEN_ESTADOS.indexOf(pedido.estado);
    return idx === -1 ? [] : ORDEN_ESTADOS.slice(idx + 1);
  }, [pedido]);

  if (cargando) return <p className="text-sm text-slate-400 py-10 text-center">Cargando...</p>;

  return (
    <div data-testid="pedido-especial-detalle-page" className="space-y-4">
      <div className="flex items-center gap-3">
        <button data-testid="pedido-volver-btn" onClick={() => navigate("/pedidos-especiales")}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200">
          <ArrowLeft size={16} />
        </button>
        <h1 className="font-heading text-2xl font-bold">
          {esNuevo ? "Nuevo pedido especial" : `Pedido especial ${pedido?.numero_fmt || ""}`}
        </h1>
        {!esNuevo && pedido && (
          <span data-testid="pedido-estado-badge"
            className={`text-xs font-semibold uppercase px-2 py-1 rounded-full ${
              pedido.estado === "cancelado" ? "bg-slate-200 text-slate-600"
                : pedido.atrasado ? "bg-pink-100 text-pink-700" : "bg-indigo-50 text-indigo-700"}`}>
            {ESTADO_PEDIDO_LABEL[pedido.estado]}
          </span>
        )}
      </div>

      {!esNuevo && pedido && pedido.fecha_estimada_arribo && pedido.estado !== "cancelado"
        && pedido.estado !== "recibido" && pedido.estado !== "entregado" && (
        <div data-testid="pedido-atraso-banner"
          className={`rounded-lg border px-4 py-2 text-sm ${
            pedido.atrasado ? "border-pink-200 bg-pink-50 text-pink-700" : "border-slate-200 bg-slate-50 text-slate-600"}`}>
          {pedido.atrasado
            ? `ATRASADO ${Math.abs(pedido.dias_para_arribo)} día(s) (estimado ${fmtFechaCorta(pedido.fecha_estimada_arribo)})`
            : `Faltan ${pedido.dias_para_arribo} día(s) para el arribo (${fmtFechaCorta(pedido.fecha_estimada_arribo)})`}
        </div>
      )}

      <Panel titulo="Datos del pedido" testId="pedido-datos-panel">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label>Vehículo *</Label>
            <Combobox testId="pedido-vehiculo-select" value={vehiculoId} onChange={onVehiculoChange}
              options={vehiculoOpciones} placeholder="Seleccione un vehículo..." searchPlaceholder="Buscar placa..." />
            {vehiculoSel?.cliente && <p className="text-xs text-slate-400 mt-1">Cliente: {vehiculoSel.cliente.nombre}</p>}
          </div>
          <div className="space-y-1">
            <Label>Trabajo ligado (opcional)</Label>
            <Combobox testId="pedido-trabajo-select" value={trabajoId} onChange={setTrabajoId}
              options={trabajoOpciones} placeholder="Ninguno" searchPlaceholder="Buscar orden..." />
          </div>
          <div className="space-y-1">
            <Label>Fecha estimada de arribo</Label>
            <Input data-testid="pedido-fecha-estimada-input" type="date" value={fechaEstimada || ""}
              onChange={(e) => setFechaEstimada(e.target.value)} disabled={!puedeEditar} />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label>Notas</Label>
            <Textarea data-testid="pedido-notas-input" value={notas} onChange={(e) => setNotas(e.target.value)} disabled={!puedeEditar} />
          </div>
        </div>
      </Panel>

      <Panel titulo="Repuestos" testId="pedido-repuestos-panel">
        {puedeEditar && (
          <div className="mb-3 flex flex-wrap items-end gap-2">
            <Button type="button" variant="outline" size="sm" data-testid="pedido-agregar-catalogo-btn" onClick={() => setPickerOpen(true)}>
              <PackagePlus size={14} className="mr-1.5" /> Agregar del catálogo
            </Button>
            <Input data-testid="pedido-manual-codigo-input" placeholder="Código" value={manual.codigo}
              onChange={(e) => setManual((m) => ({ ...m, codigo: e.target.value }))} className="w-28" />
            <Input data-testid="pedido-manual-nombre-input" placeholder="Nombre del repuesto" value={manual.nombre}
              onChange={(e) => setManual((m) => ({ ...m, nombre: e.target.value }))} className="w-56" />
            <Button type="button" variant="outline" size="sm" data-testid="pedido-manual-agregar-btn" onClick={agregarManual}>
              <Plus size={14} className="mr-1" /> Agregar manual
            </Button>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="pedido-repuestos-tabla">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                <th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Nombre</th>
                <th className="py-2 pr-3">Cant.</th><th className="py-2 pr-3">Precio unit. (IVA)</th>
                <th className="py-2 pr-3">Subtotal</th>
                {!esNuevo && <th className="py-2 pr-3">Recibido</th>}
                {puedeEditar && <th className="py-2"></th>}
              </tr>
            </thead>
            <tbody>
              {repuestos.map((r, i) => (
                <tr key={r.id || `tmp-${i}`} data-testid={`pedido-repuesto-row-${i}`} className="border-b border-slate-100">
                  <td className="py-2 pr-3 font-mono text-xs text-slate-500">{r.codigo || "—"}</td>
                  <td className="py-2 pr-3">{r.nombre}</td>
                  <td className="py-2 pr-3 w-20">
                    <Input type="number" min="1" data-testid={`pedido-repuesto-cantidad-${i}`} value={r.cantidad}
                      disabled={!puedeEditar} onChange={(e) => setRepCampo(i, "cantidad", Number(e.target.value) || 1)} className="w-16 h-8" />
                  </td>
                  <td className="py-2 pr-3 w-32">
                    <Input type="number" min="0" step="0.01" data-testid={`pedido-repuesto-precio-${i}`} value={r.precio_unitario_iva}
                      disabled={!puedeEditar} onChange={(e) => setRepCampo(i, "precio_unitario_iva", Number(e.target.value) || 0)} className="w-28 h-8" />
                  </td>
                  <td className="py-2 pr-3 font-medium">{colones(Number(r.cantidad || 0) * Number(r.precio_unitario_iva || 0))}</td>
                  {!esNuevo && (
                    <td className="py-2 pr-3">
                      <input type="checkbox" data-testid={`pedido-repuesto-recibido-${i}`} checked={!!r.recibido}
                        disabled={!tienePermiso("pedidos.editar") || pedido?.estado === "cancelado"}
                        onChange={(e) => (r.id ? marcarRecibido(r.id, e.target.checked) : null)} className="accent-indigo-600 w-4 h-4" />
                    </td>
                  )}
                  {puedeEditar && (
                    <td className="py-2">
                      <button type="button" data-testid={`pedido-repuesto-quitar-${i}`} onClick={() => quitarRepuesto(i)}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-pink-50 text-pink-600 hover:bg-pink-100">
                        <Trash2 size={13} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {repuestos.length === 0 && (
                <tr><td colSpan={7} className="py-6 text-center text-slate-400" data-testid="pedido-repuestos-vacio">
                  Agregue al menos un repuesto
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-3 flex justify-end text-sm font-semibold text-slate-700" data-testid="pedido-total">
          Total: {colones(total)}
        </div>
        {!esNuevo && puedeEditar && (
          <div className="mt-3 flex justify-end">
            <Button data-testid="pedido-guardar-cambios-btn" onClick={guardarCambios} disabled={guardando}>
              {guardando ? "Guardando..." : "Guardar cambios"}
            </Button>
          </div>
        )}
        <RepuestoPickerDialog open={pickerOpen} onOpenChange={setPickerOpen}
          modeloId={vehiculoSel?.modelo?.id} modeloNombre={vehiculoSel?.modelo?.nombre_mostrar}
          seleccion={repuestos.filter((r) => r.repuesto_id).map((r) =>
            ({ ref_id: r.repuesto_id, codigo: r.codigo, nombre: r.nombre, cantidad: r.cantidad }))}
          onConfirmar={agregarDelCatalogo} />
      </Panel>

      {esNuevo && (
        <Panel titulo="Adelanto (opcional)" testId="pedido-adelanto-panel">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Monto *</Label>
              <Input type="number" min="0.01" step="0.01" data-testid="pedido-adelanto-monto-input"
                value={adelantoMonto} onChange={(e) => setAdelantoMonto(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Fecha *</Label>
              <Input type="date" data-testid="pedido-adelanto-fecha-input" value={adelantoFecha}
                onChange={(e) => setAdelantoFecha(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Forma de pago</Label>
              <Input data-testid="pedido-adelanto-forma-pago-input" placeholder="Efectivo, SINPE, tarjeta..."
                value={adelantoFormaPago} onChange={(e) => setAdelantoFormaPago(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Comprobante (opcional)</Label>
              <Input type="file" accept="image/png,image/jpeg,image/webp,application/pdf"
                data-testid="pedido-adelanto-comprobante-input" onChange={(e) => setComprobante(e.target.files?.[0] || null)} />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label>Nota</Label>
              <Input data-testid="pedido-adelanto-nota-input" value={adelantoNota} onChange={(e) => setAdelantoNota(e.target.value)} />
            </div>
          </div>
        </Panel>
      )}

      {esNuevo && (
        <div className="flex justify-end">
          <Button data-testid="pedido-crear-btn" onClick={intentarCrear} disabled={guardando}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-8">
            {guardando ? "Creando..." : "Crear pedido especial"}
          </Button>
        </div>
      )}

      {!esNuevo && pedido && (
        <Panel titulo="Pagos" testId="pedido-pagos-panel">
          <div className="mb-3 grid grid-cols-3 gap-3 text-sm">
            <div><p className="text-slate-400">Total</p><p className="font-bold text-slate-800" data-testid="pedido-resumen-total">{colones(pedido.total)}</p></div>
            <div><p className="text-slate-400">Pagado</p><p className="font-bold text-teal-700" data-testid="pedido-resumen-pagado">{colones(pedido.pagado)}</p></div>
            <div><p className="text-slate-400">Saldo</p><p className="font-bold text-pink-700" data-testid="pedido-resumen-saldo">{colones(pedido.saldo)}</p></div>
          </div>
          <div className="space-y-2">
            {pedido.pagos.map((p) => (
              <div key={p.id} data-testid={`pedido-pago-row-${p.id}`} className="flex items-center gap-3 rounded-lg border border-slate-200 p-2.5 text-sm">
                <span className="chip rounded-full bg-slate-100 text-slate-600 uppercase">{p.tipo}</span>
                <span className="font-semibold">{colones(p.monto)}</span>
                <span className="text-slate-400">{fmtFechaCorta(p.fecha)}</span>
                <span className="text-slate-400">{p.forma_pago || "—"}</span>
                {p.comprobante_id && (
                  <button type="button" data-testid={`pedido-pago-ver-comprobante-${p.id}`} onClick={() => verComprobante(p.comprobante_id)}
                    className="ml-auto text-xs font-medium text-indigo-600 hover:underline">
                    Ver comprobante
                  </button>
                )}
              </div>
            ))}
          </div>
          {tienePermiso("pedidos.pagos") && pedido.estado !== "cancelado" && (
            <div className="mt-3">
              <Button variant="outline" size="sm" data-testid="pedido-registrar-pago-btn" onClick={() => setPagoDialog(true)}>
                <Plus size={14} className="mr-1.5" /> Registrar pago
              </Button>
            </div>
          )}
        </Panel>
      )}

      {!esNuevo && pedido && (
        <Panel titulo="Estado e historial" testId="pedido-estado-panel">
          {pedido.estado !== "cancelado" && tienePermiso("pedidos.editar") && siguientesEstados.length > 0 && (
            <div className="mb-4 flex flex-wrap items-end gap-2">
              <div className="space-y-1">
                <Label>Nuevo estado</Label>
                <select data-testid="pedido-estado-select" value={estadoNuevo} onChange={(e) => setEstadoNuevo(e.target.value)}
                  className="h-9 rounded-md border border-slate-200 px-3 text-sm bg-white">
                  <option value="">Seleccione...</option>
                  {siguientesEstados.map((e) => <option key={e} value={e}>{ESTADO_PEDIDO_LABEL[e]}</option>)}
                </select>
              </div>
              <div className="space-y-1 flex-1 min-w-[200px]">
                <Label>Nota (opcional)</Label>
                <Input data-testid="pedido-estado-nota-input" value={estadoNota} onChange={(e) => setEstadoNota(e.target.value)} />
              </div>
              <Button data-testid="pedido-cambiar-estado-btn" onClick={cambiarEstado} disabled={!estadoNuevo}>Cambiar estado</Button>
            </div>
          )}
          <div className="space-y-2">
            {[...(pedido.historial || [])].reverse().map((h, i) => (
              <div key={i} data-testid={`pedido-historial-item-${i}`}
                className="flex items-start gap-2 text-sm border-t border-slate-50 pt-2 first:border-0 first:pt-0">
                <History size={14} className="text-slate-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-slate-700">
                    {h.estado_anterior
                      ? `${ESTADO_PEDIDO_LABEL[h.estado_anterior] || h.estado_anterior} → ${ESTADO_PEDIDO_LABEL[h.estado_nuevo] || h.estado_nuevo}`
                      : ESTADO_PEDIDO_LABEL[h.estado_nuevo] || h.estado_nuevo}
                    {h.nota && <span className="text-slate-400"> · {h.nota}</span>}
                  </p>
                  <p className="text-xs text-slate-400">{h.usuario} · {new Date(h.fecha).toLocaleString("es-CR")}</p>
                </div>
              </div>
            ))}
          </div>
          {pedido.estado !== "cancelado" && tienePermiso("pedidos.cancelar") && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <Button variant="outline" size="sm" data-testid="pedido-cancelar-btn" onClick={() => setCancelarOpen(true)}
                className="border-pink-300 text-pink-700 hover:bg-pink-50">
                <Ban size={14} className="mr-1.5" /> Cancelar pedido
              </Button>
            </div>
          )}
        </Panel>
      )}

      <Dialog open={sinAdelantoOpen} onOpenChange={setSinAdelantoOpen}>
        <DialogContent className="bg-white" data-testid="pedido-sin-adelanto-dialog">
          <DialogHeader><DialogTitle>¿Crear el pedido especial sin adelanto?</DialogTitle></DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSinAdelantoOpen(false)}>Cancelar</Button>
            <Button data-testid="pedido-sin-adelanto-confirmar-btn" onClick={crear} disabled={guardando}>
              {guardando ? "Creando..." : "Crear sin adelanto"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pagoDialog} onOpenChange={setPagoDialog}>
        <DialogContent className="bg-white" data-testid="pedido-pago-dialog">
          <DialogHeader><DialogTitle>Registrar pago</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Tipo</Label>
              <select data-testid="pedido-pago-tipo-select" value={pagoTipo} onChange={(e) => setPagoTipo(e.target.value)}
                className="h-9 w-full rounded-md border border-slate-200 px-3 text-sm bg-white">
                <option value="abono">Abono</option>
                <option value="final">Pago final</option>
              </select>
            </div>
            <div className="space-y-1"><Label>Monto *</Label>
              <Input type="number" min="0.01" step="0.01" data-testid="pedido-pago-monto-input" value={pagoMonto} onChange={(e) => setPagoMonto(e.target.value)} /></div>
            <div className="space-y-1"><Label>Fecha *</Label>
              <Input type="date" data-testid="pedido-pago-fecha-input" value={pagoFecha} onChange={(e) => setPagoFecha(e.target.value)} /></div>
            <div className="space-y-1"><Label>Forma de pago</Label>
              <Input data-testid="pedido-pago-forma-pago-input" value={pagoFormaPago} onChange={(e) => setPagoFormaPago(e.target.value)} /></div>
            <div className="space-y-1"><Label>Nota</Label>
              <Input data-testid="pedido-pago-nota-input" value={pagoNota} onChange={(e) => setPagoNota(e.target.value)} /></div>
            <div className="space-y-1"><Label>Comprobante (opcional)</Label>
              <Input type="file" accept="image/png,image/jpeg,image/webp,application/pdf" data-testid="pedido-pago-comprobante-input"
                onChange={(e) => setPagoComprobante(e.target.files?.[0] || null)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPagoDialog(false)}>Cancelar</Button>
            <Button data-testid="pedido-pago-confirmar-btn" onClick={registrarPago} disabled={registrandoPago}>
              {registrandoPago ? "Guardando..." : "Registrar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelarOpen} onOpenChange={setCancelarOpen}>
        <DialogContent className="bg-white" data-testid="pedido-cancelar-dialog">
          <DialogHeader><DialogTitle>Cancelar pedido especial</DialogTitle></DialogHeader>
          <div className="space-y-1">
            <Label>Motivo (opcional)</Label>
            <Textarea data-testid="pedido-cancelar-nota-input" value={cancelarNota} onChange={(e) => setCancelarNota(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelarOpen(false)}>Volver</Button>
            <Button data-testid="pedido-cancelar-confirmar-btn" onClick={cancelar} className="bg-pink-600 hover:bg-pink-700 text-white">
              Confirmar cancelación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
